'use client';

import { useState } from 'react';
import { Loader2, Mail } from 'lucide-react';

type Variant = 'card' | 'band' | 'footer';

interface NewsletterSignupProps {
  /** Visual treatment: navy card (blog/podcast), full navy band (homepage), slim navy card (footer). */
  variant: Variant;
  /** Canonical email_subscribers source (see src/lib/email/subscriber-sources.ts). */
  source?: string;
  /** Placement attribution, e.g. ['placement-blog'] — source stays canonical, tags carry the where. */
  tags?: string[];
  className?: string;
}

/**
 * "A little hope in your inbox" — Aubree's newsletter signup, reusable across
 * the public site. Posts to /api/newsletter/subscribe (dedup + unsubscribe
 * rules live server-side; new subscribers get the welcome email).
 */
export default function NewsletterSignup({ variant, source = 'newsletter_form', tags = [], className = '' }: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source, tags, marketing_consent: consent }),
      });
      const result = await res.json();
      if (!res.ok || !result.success) throw new Error(result.error || 'Something went wrong. Please try again.');
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const heading = (
    <>
      A little <span className="text-soft-teal italic">hope</span> in your inbox
    </>
  );

  const form = done ? (
    <p className="text-[0.98rem] font-medium text-white">
      You&apos;re in. A note from me is on its way to your inbox.
    </p>
  ) : (
    <form onSubmit={submit} className="w-full">
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <div className="relative flex-1">
          <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            aria-label="Email address for the newsletter"
            disabled={busy}
            className="h-12 w-full rounded-full border border-white/20 bg-white/10 pl-11 pr-4 text-[0.95rem] text-white outline-none transition-all placeholder:text-white/50 focus:border-soft-teal focus:bg-white/15 focus:shadow-[0_0_0_3px_rgba(79,163,165,0.25)]"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="h-12 shrink-0 rounded-full bg-soft-teal px-7 text-[0.95rem] font-semibold text-deep-navy shadow-[0_4px_14px_rgba(79,163,165,0.4)] transition-all hover:-translate-y-0.5 hover:bg-soft-teal-light hover:shadow-[0_6px_20px_rgba(79,163,165,0.5)] disabled:opacity-60"
        >
          {busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : 'Count me in'}
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-blush">{error}</p>}
      <label className="mt-3 flex items-start justify-center gap-2 text-xs text-white/55 sm:justify-start">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 rounded border-white/30 accent-soft-teal"
        />
        <span>I&apos;d like mold recovery insights and updates from Aubree. Unsubscribe anytime.</span>
      </label>
    </form>
  );

  const glows = (
    <>
      <div className="pointer-events-none absolute right-[-80px] top-[-80px] h-[280px] w-[280px] bg-[radial-gradient(circle,_#4FA3A5_0%,_transparent_70%)] opacity-20" />
      <div className="pointer-events-none absolute bottom-[-80px] left-[-80px] h-[280px] w-[280px] bg-[radial-gradient(circle,_#E6B7C1_0%,_transparent_70%)] opacity-15" />
    </>
  );

  if (variant === 'footer') {
    return (
      <div className={`relative mb-10 overflow-hidden rounded-[15px] bg-deep-navy px-6 py-8 sm:px-10 ${className}`}>
        {glows}
        <div className="relative z-10 flex flex-col items-center gap-6 text-center lg:flex-row lg:items-center lg:gap-12 lg:text-left">
          <div className="lg:max-w-[380px]">
            <h2 className="mb-1.5 font-headline text-[1.6rem] font-medium leading-tight text-white">{heading}</h2>
            <p className="text-sm leading-relaxed text-white/70">
              One email a week from me, with what actually helps families get better.
            </p>
          </div>
          <div className="w-full max-w-[480px] flex-1">{form}</div>
        </div>
      </div>
    );
  }

  const inner = (
    <div className="relative z-10 mx-auto max-w-[560px] text-center">
      <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-blush/20">
        <Mail className="h-5 w-5 text-blush" />
      </div>
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-soft-teal">The newsletter</p>
      <h2 className="mb-3 font-headline text-[clamp(1.7rem,3.2vw,2.4rem)] font-medium leading-[1.2] text-white">
        {heading}
      </h2>
      <p className="mb-7 text-[0.98rem] leading-relaxed text-white/75">
        One email a week from me, with what actually helps families get better. No spam, unsubscribe anytime.
      </p>
      {form}
    </div>
  );

  if (variant === 'band') {
    return (
      <section className={`relative overflow-hidden bg-deep-navy px-[4%] py-20 ${className}`}>
        {glows}
        {inner}
      </section>
    );
  }

  // card
  return (
    <div className={`relative overflow-hidden rounded-[15px] border border-white/10 bg-deep-navy px-6 py-12 sm:px-10 ${className}`}>
      {glows}
      {inner}
    </div>
  );
}
